import { useCallback, useEffect, useState } from 'react';
import type { Trip, TripRoute, TripTransition } from '../types/trip';
import type { AlertEvent } from '../types/alert';
import type { LogbookEntry } from '../types/logbook';
import type { Expense } from '../types/expense';
import type { Revenue } from '../types/finance';
import { getTripRoute, getAllTripAlerts, getTripTransitions, getTripLogbookEntries, getTripExpenses, getTripRevenues } from '../lib/api/endpoints';
import { tripRouteFromDto, tripTransitionsFromDto, alertFromDto, logbookEntryFromDto, expenseFromDto, revenueFromDto } from '../lib/api/mappers';
import { useAuth } from '../context/AuthContext';
import { puedeVerIngresos } from '../utils/finance';

/**
 * FE-11 — el expediente derivado de un viaje: ruta, alertas, cambios del viaje,
 * novedades (desde FE-13), gastos (desde FE-12) e ingresos (desde FE-32).
 *
 * Todos se piden juntos porque salen de la MISMA ventana
 * (ADR-024), y por lo mismo **no se cachean por id de viaje**: corregir el
 * viaje cambia las tres respuestas sin tocar una sola fila de telemetría. El
 * `dependeDe` de abajo es esa regla hecha código — cuando la ventana del viaje
 * cambia, esto se vuelve a pedir solo.
 *
 * Un viaje SIN ventana (cancelado, o el `0001-01-01` del issue #93) no tiene
 * trayecto ni alertas que pedir: no hay nada dentro de una ventana vacía. Se
 * devuelven vacíos sin llamar a la API — dos 200 con listas vacías cuestan lo
 * mismo que no preguntarlos, pero preguntarlos sugiere que la respuesta podría
 * cambiar.
 *
 * ⚠️ Las NOVEDADES y los GASTOS sí se piden siempre, incluso sin ventana, y esa
 * asimetría es deliberada: los dos pueden llevar el `tripId` ESTAMPADO por
 * alguien, y esa declaración manda sobre la ventana (ADR-024). Un viaje
 * cancelado con una varada asignada a mano —o con el tanqueo que se hizo antes
 * de cancelarlo— tiene expediente aunque no tenga ni un metro de trayecto, y es
 * justo el caso en que a alguien le interesa mirarlo.
 *
 * ⚠️ Los INGRESOS solo se piden si el rol puede verlos. Es un `Promise.all`: una
 * sola petición que falla rechaza el grupo entero y el `catch` de abajo vacía
 * TODO el expediente. Desde el #225 el Operador recibe 403 en `GET /revenues`,
 * así que pedírselos le borraba cambios, novedades, gastos, ruta y alertas —el
 * expediente completo— por una lista que de todos modos no iba a ver (#228).
 * Es el mismo fallo que el MR !29: `Promise.all` + un 403 de rol = pantalla en
 * blanco donde solo faltaba un pedazo. El rol se lee aquí, no se recibe por
 * parámetro, para que ningún consumidor futuro pueda olvidarse de pasarlo.
 */
export function useTripDetail(trip: Trip | null) {
  const { user } = useAuth();
  const verIngresos = puedeVerIngresos(user);
  const [route, setRoute] = useState<TripRoute | null>(null);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [transitions, setTransitions] = useState<TripTransition[]>([]);
  const [logbook, setLogbook] = useState<LogbookEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const tripId = trip?.id ?? null;
  // La ventana forma parte de la dependencia a propósito: tras una corrección
  // el id es el mismo y el expediente ya no.
  const dependeDe = trip?.window ? `${trip.window.from}|${trip.window.to}` : '';

  useEffect(() => {
    if (!tripId) {
      setRoute(null);
      setAlerts([]);
      setTransitions([]);
      setLogbook([]);
      setExpenses([]);
      setRevenues([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);

      // `allSettled`, NO `all`: cada sección del expediente se resuelve por su
      // cuenta. Con `all` bastaba UNA petición fallida para que el `catch`
      // vaciara las seis y el modal dijera «No se pudo cargar el expediente»
      // teniendo cinco respuestas buenas en la mano. No es hipotético: en una
      // sola tarde se vio dos veces —`GET /revenues` devolviendo 403 al
      // Operador desde el #225, y `GET /trips/{id}/route` devolviendo 404 al
      // Operador y 200 al Administrador sobre el MISMO viaje—, y es el mismo
      // fallo que el MR !29 con `/devices`. Un expediente al que le falta un
      // pedazo se muestra sin ese pedazo; solo es un error cuando no llega
      // nada.
      const [transitionsRes, logbookRes, expensesRes, revenuesRes, routeRes, alertsRes] = await Promise.allSettled([
        getTripTransitions(tripId),
        getTripLogbookEntries(tripId),
        getTripExpenses(tripId),
        verIngresos ? getTripRevenues(tripId) : Promise.resolve({ items: [] }),
        dependeDe ? getTripRoute(tripId) : Promise.resolve(null),
        dependeDe ? getAllTripAlerts(tripId) : Promise.resolve([]),
      ]);
      if (cancelled) return;

      setTransitions(transitionsRes.status === 'fulfilled' ? tripTransitionsFromDto(transitionsRes.value.items) : []);
      setLogbook(logbookRes.status === 'fulfilled' ? logbookRes.value.items.map(logbookEntryFromDto) : []);
      setExpenses(expensesRes.status === 'fulfilled' ? expensesRes.value.items.map(expenseFromDto) : []);
      setRevenues(revenuesRes.status === 'fulfilled' ? revenuesRes.value.items.map(revenueFromDto) : []);
      setRoute(routeRes.status === 'fulfilled' && routeRes.value ? tripRouteFromDto(routeRes.value) : null);
      setAlerts(alertsRes.status === 'fulfilled' ? alertsRes.value.map(alertFromDto) : []);

      // Solo es un error del expediente si NINGUNA parte llegó: entonces no es
      // "falta un pedazo", es que no hay expediente que mostrar.
      const partes = [transitionsRes, logbookRes, expensesRes, revenuesRes, routeRes, alertsRes];
      const todasFallaron = partes.every(r => r.status === 'rejected');
      const primerFallo = partes.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
      setError(todasFallaron && primerFallo
        ? (primerFallo.reason instanceof Error ? primerFallo.reason : new Error('Error desconocido cargando el viaje'))
        : null);

      setIsLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [tripId, dependeDe, reloadToken, verIngresos]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  return { route, alerts, transitions, logbook, expenses, revenues, isLoading, error, reload };
}
